import { useMemo } from "react";
import { useParams } from "react-router";
import _ from "lodash";

import {
  AttachFileIcon,
  CalendarTodayIcon,
  ChatOutlinedIcon,
  NotesIcon,
} from "icons";
import { colors } from "theme/palette";

import ProgressBar from "components/ProgressBar";
import { EMPTY_VALUE_REGEX } from "components/RichTextEditor/constants";
import Stack from "components/Stack";
import Text from "components/Text";

import { useDueDiligenceFilters } from "pages/Deal/DueDiligence/Filters";

import { useDealChecklistPermission } from "entities/Deal/sdk";
import LightningIcon from "entities/Field/components/FieldUpdateIcon/assets/lightningIcon.svg";
import { EFieldStatus } from "entities/Field/constants";
import { FieldType } from "entities/Field/sdk";
import FieldStatusIcon from "entities/Package/components/FieldStatusIcon";
import { IPackageTab } from "entities/Package/sdk";
import { useTaskChannels } from "entities/Task/sdk";

import { useChecklistTabProgress } from "./hooks";
import Toggle from "./Toggle";

export interface IFilteredViews {
  tab?: IPackageTab;
}

const FilteredViews = ({ tab }: IFilteredViews) => {
  const params = useParams<{
    dealId: string;
    packageId: string;
  }>();

  const dealId = Number(params.dealId);
  const packageId = Number(params.packageId);

  const { data: taskChannels } = useTaskChannels({ dealId });
  const { data: userPermission } = useDealChecklistPermission({
    dealId,
    packageId,
  });
  const hasWriteAccess = useMemo(
    () => !_.isNil(userPermission) && userPermission.has_write_access,
    [userPermission]
  );
  const isChecklistOwner = useMemo(
    () => !_.isNil(userPermission) && userPermission.is_checklist_owner,
    [userPermission]
  );

  const {
    statusFilter,
    showOnlyUpdatedFields,
    showOnlyFileFields,
    showOnlyFieldsWithRemarks,
    showOnlyFieldsWithTasks,
    toggleStatusFilter,
    toggleShowOnlyUpdatedFields,
    toggleShowOnlyFileFields,
    toggleShowOnlyFieldsWithRemarks,
    toggleShowOnlyFieldsWithTasks,
    showOnlyFieldsWithUnreadMessages,
    toggleShowOnlyFieldsWithUnreadMessages,
  } = useDueDiligenceFilters();

  const numberOfFieldsMatchingFileFieldsFilter = useMemo(() => {
    let totalCount = 0;

    if (!_.isNil(tab)) {
      tab.categories.forEach((category) => {
        category.sections.forEach((section) => {
          const filteredFields = section.fields.filter(
            (field) => field.field_type === FieldType.FILE
          );

          totalCount += filteredFields.length;
        });
      });
    }

    return totalCount;
  }, [tab]);

  const numberOfFieldsMatchingRemarksFilter = useMemo(() => {
    let totalCount = 0;

    if (!_.isNil(tab)) {
      tab.categories.forEach((category) => {
        category.sections.forEach((section) => {
          const filteredFields = section.fields.filter((field) => {
            const sharedRemark = _.first(field.shared_remarks);
            const formattedSharedRemark = EMPTY_VALUE_REGEX.test(
              sharedRemark?.shared_remark || ""
            )
              ? ""
              : sharedRemark?.shared_remark;
            const privateRemark = _.first(field.private_remarks);
            const formattedPrivateRemark = EMPTY_VALUE_REGEX.test(
              privateRemark?.private_remark || ""
            )
              ? ""
              : privateRemark?.private_remark;

            return (
              !_.isEmpty(formattedSharedRemark) ||
              !_.isEmpty(formattedPrivateRemark)
            );
          });

          totalCount += filteredFields.length;
        });
      });
    }

    return totalCount;
  }, [tab]);

  const numberOfFieldsMatchingUpdatedFieldsFilter = useMemo(() => {
    let totalCount = 0;

    if (!_.isNil(tab)) {
      tab.categories.forEach((category) => {
        category.sections.forEach((section) => {
          const filteredFields = section.fields.filter(
            (field) => field.has_new
          );

          totalCount += filteredFields.length;
        });
      });
    }

    return totalCount;
  }, [tab]);

  const [
    numberOfFieldsMatchingOpenTasksFilter,
    numberOfFieldsMatchingUnreadMessagesFilter,
  ] = useMemo(() => {
    let totalCountOfFieldsForTasks = 0;
    let totalCountOfFieldsForUnreadMessages = 0;

    if (!_.isNil(tab) && !_.isNil(taskChannels)) {
      tab.categories.forEach((category) => {
        category.sections.forEach((section) => {
          section.fields.forEach((field) => {
            const tasksForField = taskChannels?.filter(
              (taskChannel) =>
                taskChannel.task.company_category?.id ===
                  category.category_id &&
                taskChannel.task.company_section?.id === section.section_id &&
                taskChannel.task.company_field?.id === field.field_id &&
                !taskChannel.task.completed
            );

            if (tasksForField.length > 0) {
              totalCountOfFieldsForTasks++;
            }

            if (
              tasksForField.some((taskChannel) => taskChannel.unread_count > 0)
            ) {
              totalCountOfFieldsForUnreadMessages++;
            }
          });
        });
      });
    }

    return [totalCountOfFieldsForTasks, totalCountOfFieldsForUnreadMessages];
  }, [tab, taskChannels]);

  const {
    satisfiedCount,
    inReviewCount,
    doneCount,
    notDoneCount,
    naCount,
    progressData,
  } = useChecklistTabProgress({ tab });

  return (
    <Stack gap={2} data-testid="filters-and-statuses">
      {hasWriteAccess && (
        <Stack gap={4} direction="row" alignItems="flex-end" flexWrap="wrap">
          <Stack gap={0.5}>
            <Text variant="labelSmall" color={colors.gray40} fontWeight={500}>
              statuses
            </Text>
            <Stack direction="row" gap={0.5}>
              {isChecklistOwner ? (
                <>
                  <Toggle
                    onClick={() =>
                      toggleStatusFilter({ statuses: [EFieldStatus.SATISFIED] })
                    }
                    isOn={statusFilter.includes(EFieldStatus.SATISFIED)}
                    count={satisfiedCount}
                    icon={
                      <FieldStatusIcon
                        status={EFieldStatus.SATISFIED}
                        sx={{ width: "16px", height: "16px" }}
                      />
                    }
                  >
                    {EFieldStatus.SATISFIED}
                  </Toggle>

                  <Toggle
                    onClick={() =>
                      toggleStatusFilter({ statuses: [EFieldStatus.IN_REVIEW] })
                    }
                    isOn={statusFilter.includes(EFieldStatus.IN_REVIEW)}
                    count={inReviewCount}
                    icon={
                      <FieldStatusIcon
                        status={EFieldStatus.IN_REVIEW}
                        sx={{ width: "16px", height: "16px" }}
                      />
                    }
                  >
                    {EFieldStatus.IN_REVIEW}
                  </Toggle>

                  <Toggle
                    onClick={() =>
                      toggleStatusFilter({ statuses: [EFieldStatus.DONE] })
                    }
                    isOn={statusFilter.includes(EFieldStatus.DONE)}
                    count={doneCount}
                    icon={
                      <FieldStatusIcon
                        status={EFieldStatus.DONE}
                        sx={{ width: "16px", height: "16px" }}
                      />
                    }
                  >
                    {EFieldStatus.DONE}
                  </Toggle>

                  <Toggle
                    onClick={() =>
                      toggleStatusFilter({ statuses: [EFieldStatus.NOT_DONE] })
                    }
                    isOn={statusFilter.includes(EFieldStatus.NOT_DONE)}
                    count={notDoneCount}
                    icon={
                      <FieldStatusIcon
                        status={EFieldStatus.NOT_DONE}
                        sx={{ width: "16px", height: "16px" }}
                      />
                    }
                  >
                    {EFieldStatus.NOT_DONE}
                  </Toggle>

                  <Toggle
                    onClick={() =>
                      toggleStatusFilter({ statuses: [EFieldStatus.NA] })
                    }
                    isOn={statusFilter.includes(EFieldStatus.NA)}
                    count={naCount}
                    icon={
                      <FieldStatusIcon
                        status={EFieldStatus.NA}
                        sx={{ width: "16px", height: "16px" }}
                      />
                    }
                  >
                    {EFieldStatus.NA}
                  </Toggle>
                </>
              ) : (
                <>
                  <Toggle
                    onClick={() =>
                      toggleStatusFilter({
                        statuses: [
                          EFieldStatus.DONE,
                          EFieldStatus.IN_REVIEW,
                          EFieldStatus.SATISFIED,
                        ],
                      })
                    }
                    isOn={[
                      EFieldStatus.DONE,
                      EFieldStatus.IN_REVIEW,
                      EFieldStatus.SATISFIED,
                    ].every((item) => statusFilter.includes(item))}
                    count={doneCount + inReviewCount + satisfiedCount}
                    icon={
                      <FieldStatusIcon
                        status={EFieldStatus.DONE}
                        sx={{ width: "16px", height: "16px" }}
                      />
                    }
                  >
                    {EFieldStatus.DONE}
                  </Toggle>

                  <Toggle
                    onClick={() =>
                      toggleStatusFilter({ statuses: [EFieldStatus.NOT_DONE] })
                    }
                    isOn={statusFilter.includes(EFieldStatus.NOT_DONE)}
                    count={notDoneCount}
                    icon={
                      <FieldStatusIcon
                        status={EFieldStatus.NOT_DONE}
                        sx={{ width: "16px", height: "16px" }}
                      />
                    }
                  >
                    {EFieldStatus.NOT_DONE}
                  </Toggle>

                  <Toggle
                    onClick={() =>
                      toggleStatusFilter({ statuses: [EFieldStatus.NA] })
                    }
                    isOn={statusFilter.includes(EFieldStatus.NA)}
                    count={naCount}
                    icon={
                      <FieldStatusIcon
                        status={EFieldStatus.NA}
                        sx={{ width: "16px", height: "16px" }}
                      />
                    }
                  >
                    {EFieldStatus.NA}
                  </Toggle>
                </>
              )}
            </Stack>
          </Stack>
          {hasWriteAccess && <ProgressBar progressData={progressData} />}
        </Stack>
      )}

      <Stack gap={0.5} data-testid="filters-section">
        <Text variant="labelSmall" color={colors.gray40} fontWeight={500}>
          filters
        </Text>

        <Stack direction="row" gap={0.5} flexWrap="wrap">
          <Toggle
            onClick={toggleShowOnlyUpdatedFields}
            isOn={showOnlyUpdatedFields}
            count={numberOfFieldsMatchingUpdatedFieldsFilter}
            icon={
              <Stack
                sx={{
                  backgroundColor: colors.blue100,
                }}
                borderRadius="10px"
                alignItems="center"
                justifyContent="center"
                height="16px"
                width="16px"
              >
                <img src={LightningIcon} alt="" width="6px" height="8px" />
              </Stack>
            }
          >
            Updated
          </Toggle>

          <Toggle
            onClick={toggleShowOnlyFieldsWithRemarks}
            isOn={showOnlyFieldsWithRemarks}
            count={numberOfFieldsMatchingRemarksFilter}
            icon={
              <Stack
                sx={{
                  backgroundColor: colors.gray20,
                }}
                borderRadius="10px"
                alignItems="center"
                justifyContent="center"
                height="16px"
                width="16px"
              >
                <NotesIcon sx={{ height: "auto", width: "12px" }} />
              </Stack>
            }
          >
            Remarks
          </Toggle>

          <Toggle
            onClick={toggleShowOnlyFileFields}
            isOn={showOnlyFileFields}
            count={numberOfFieldsMatchingFileFieldsFilter}
            icon={<AttachFileIcon sx={{ height: "auto", width: "16px" }} />}
          >
            File uploads
          </Toggle>

          <Toggle
            onClick={toggleShowOnlyFieldsWithTasks}
            isOn={showOnlyFieldsWithTasks}
            count={numberOfFieldsMatchingOpenTasksFilter}
            icon={<CalendarTodayIcon sx={{ height: "auto", width: "16px" }} />}
          >
            Open tasks
          </Toggle>

          <Toggle
            onClick={toggleShowOnlyFieldsWithUnreadMessages}
            isOn={showOnlyFieldsWithUnreadMessages}
            count={numberOfFieldsMatchingUnreadMessagesFilter}
            icon={<ChatOutlinedIcon sx={{ height: "auto", width: "16px" }} />}
          >
            Unread messages
          </Toggle>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default FilteredViews;
